import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDropzone } from 'react-dropzone'
import { useQuery, useQueryClient } from 'react-query'
import { CloudArrowUpIcon, DocumentIcon, TrashIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { documentsAPI } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import Badge from '../../components/ui/Badge'
import toast from 'react-hot-toast'

const UploadPage = () => {
  const { t } = useTranslation('documents')
  const [uploading, setUploading] = useState(false)
  const [uploadedFiles, setUploadedFiles] = useState([])
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuth()

  // Pobieranie aktualnych dokumentów z odświeżaniem co 3 sekundy
  const { data: documentsData } = useQuery(
    'documents',
    () => documentsAPI.getDocuments({ limit: 10, sort: 'created_at', order: 'DESC' }),
    {
      refetchInterval: 3000, // Odświeżaj co 3 sekundy
      enabled: uploadedFiles.length > 0, // Tylko gdy są przesłane pliki
    }
  )

  const onDrop = async (acceptedFiles) => {
    setUploading(true)
    
    try {
      for (const file of acceptedFiles) {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('title', file.name.split('.')[0])

        // Użyj preferencji użytkownika dla OCR engine
        const userPreferences = user?.preferences || {}
        formData.append('ocr_engine', userPreferences.ocr_engine || 'easyocr')
        formData.append('extract_receipt_data', userPreferences.extract_receipt_data !== undefined ? userPreferences.extract_receipt_data : 'true')

        const response = await documentsAPI.uploadDocument(formData)

        if (response.success) {
          setUploadedFiles(prev => [...prev, response.data.document])
          toast.success(t('upload.uploadSuccess', { filename: file.name }))
          // Odśwież listę dokumentów
          queryClient.invalidateQueries('documents')
        }
      }
    } catch (error) {
      toast.error(t('upload.uploadError'))
    } finally {
      setUploading(false)
    }
  }

  // Funkcja usuwania dokumentu
  const handleDeleteDocument = async (documentId) => {
    try {
      await documentsAPI.deleteDocument(documentId)
      setUploadedFiles(prev => prev.filter(file => file.id !== documentId))
      queryClient.invalidateQueries('documents')
      toast.success(t('deleteSuccess'))
    } catch (error) {
      toast.error(t('deleteError'))
    }
  }

  // Funkcja pobierania aktualnego statusu dokumentu
  const getDocumentStatus = (documentId) => {
    if (documentsData?.data?.documents) {
      const document = documentsData.data.documents.find(doc => doc.id === documentId)
      return document?.ocr_status || 'processing'
    }
    return 'processing'
  }

  // Funkcja pobierania badge dla statusu
  const getStatusBadge = (status) => {
    const variants = {
      pending: 'gray',
      processing: 'warning',
      completed: 'success',
      failed: 'error'
    }

    const labels = {
      pending: t('common:status.pending'),
      processing: t('common:status.processing'),
      completed: t('common:status.completed'),
      failed: t('common:status.failed')
    }

    return (
      <Badge variant={variants[status] || 'gray'} size="sm">
        {labels[status] || status}
      </Badge>
    )
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'],
      'application/pdf': ['.pdf'],
      'text/plain': ['.txt'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    maxSize: 20 * 1024 * 1024, // 20MB
    disabled: uploading
  })

  return (
    <div className="space-y-6">
      <div className="md:flex md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-sm text-[var(--color-text-muted)]">
            {t('upload.subtitle')}
          </p>
        </div>
      </div>

      {/* Upload Area */}
      <Card>
        <Card.Body>
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors ${
              isDragActive
                ? 'border-[#1f9f93] bg-[rgba(31,159,147,0.05)]'
                : 'border-[var(--color-border)] hover:border-[rgba(29,38,45,0.20)]'
            } ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <input {...getInputProps()} />
            <CloudArrowUpIcon className="mx-auto h-12 w-12 text-[var(--color-text-faint)]" />
            <h3 className="mt-2 text-sm font-medium text-[var(--color-text)]">
              {isDragActive ? t('upload.dropHere') : t('upload.dragOrClick')}
            </h3>
            <p className="mt-1 text-sm text-[var(--color-text-muted)]">
              {t('upload.fileTypes')}
            </p>
            {uploading && (
              <p className="mt-2 text-sm text-[#1f9f93]">
                {t('upload.uploading')}
              </p>
            )}
          </div>
        </Card.Body>
      </Card>

      {/* Uploaded Files */}
      {uploadedFiles.length > 0 && (
        <Card>
          <Card.Header>
            <h3 className="text-lg font-medium text-[var(--color-text)]">
              {t('upload.uploadedFiles', { count: uploadedFiles.length })}
            </h3>
          </Card.Header>
          <Card.Body>
            <div className="space-y-3">
              {uploadedFiles.map((file) => (
                <div key={file.id} className="flex items-center justify-between p-3 bg-[var(--color-table-header)] rounded-lg">
                  <div className="flex items-center space-x-3">
                    <DocumentIcon className="h-5 w-5 text-[var(--color-text-faint)]" />
                    <div>
                      <p className="text-sm font-medium text-[var(--color-text)]">
                        {file.title}
                      </p>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className="text-xs text-[var(--color-text-muted)]">{t('upload.status')}</span>
                        {getStatusBadge(getDocumentStatus(file.id))}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/documents/${file.id}`)}
                    >
                      {t('common:view')}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteDocument(file.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Card.Body>
          <Card.Footer>
            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={() => navigate('/documents')}
              >
                {t('upload.viewAll')}
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  setUploadedFiles([])
                }}
              >
                {t('upload.uploadMore')}
              </Button>
            </div>
          </Card.Footer>
        </Card>
      )}
    </div>
  )
}

export default UploadPage
